import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { notificationAPI } from '../services/api';

export function usePushNotifications() {
  const { user } = useAuth();
  const [pushPermission, setPushPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [pushEnabled, setPushEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isIOS = typeof window !== 'undefined' &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

  const isStandalone = typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true);

  const pushSupported = typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window;

  // iOS Safari only exposes Push APIs inside an installed PWA (home screen).
  const needsInstall = isIOS && !isStandalone;

  useEffect(() => {
    if (!pushSupported || !user) return;

    let cancelled = false;

    (async () => {
      try {
        await navigator.serviceWorker.register('/sw.js');
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();

        if (cancelled) return;

        if (!existing) {
          setPushEnabled(false);
          return;
        }

        try {
          await notificationAPI.subscribePush(subscriptionToPayload(existing));
          if (!cancelled) setPushEnabled(true);
        } catch {
          await existing.unsubscribe();
          if (!cancelled) setPushEnabled(false);
        }

        if (!cancelled) setPushPermission(Notification.permission);
      } catch {}
    })();

    return () => { cancelled = true; };
  }, [pushSupported, user]);

  const subscribe = useCallback(async () => {
    if (!pushSupported || !user || loading) return;

    setLoading(true);
    setError(null);

    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== 'granted') {
        setError('Permission denied — enable notifications in your browser settings.');
        setLoading(false);
        return;
      }

      const { data } = await notificationAPI.getVapidPublicKey();
      const vapidKey = data?.public_key;
      if (!vapidKey) {
        setError('Push notifications are not configured on the server (missing VAPID key).');
        setLoading(false);
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      await notificationAPI.subscribePush(subscriptionToPayload(subscription));

      setPushEnabled(true);
      setError(null);
    } catch (err) {
      console.error('Push subscription failed:', err);
      const backendMsg = err?.response?.data?.error;
      setError(backendMsg || err.message || 'Failed to enable push notifications.');
    } finally {
      setLoading(false);
    }
  }, [pushSupported, user, loading]);

  const unsubscribe = useCallback(async () => {
    if (!pushSupported || loading) return;

    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();

      if (subscription) {
        await notificationAPI.unsubscribePush({ endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }

      setPushEnabled(false);
    } catch (err) {
      console.error('Push unsubscribe failed:', err);
    } finally {
      setLoading(false);
    }
  }, [pushSupported, loading]);

  return {
    pushSupported,
    needsInstall,
    pushPermission,
    pushEnabled,
    loading,
    error,
    subscribe,
    unsubscribe,
  };
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

// Extract endpoint + keys from a PushSubscription using getKey(),
// since .keys is not a standard property on PushSubscription.
function subscriptionToPayload(subscription) {
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
      auth: arrayBufferToBase64(subscription.getKey('auth')),
    },
  };
}

function arrayBufferToBase64(buffer) {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let i = 0; i < bytes.length; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str);
}
